import {
  CONFETTI_COLORS,
  CONFETTI_MIX,
  CONFETTI_SHAPES,
  type ConfettiKind,
  type ConfettiShape,
} from './confettiShapes'

/**
 * **Das Konfetti des Geschafft-Moments als reine Rechnung** — kein DOM, kein
 * Zeichnen. Ein Schritt nimmt die vergangene Zeit und bewegt jedes Stück;
 * `DoneMomentConfetti` zeichnet das Ergebnis.
 *
 * **Fallen:** Schwerkraft mit Luftwiderstand — ein Stück nähert sich seiner
 * Sinkgeschwindigkeit, statt immer schneller zu werden wie ein Stein. Dazu
 * pendelt es zur Seite, dreht sich und kippt (`tilt`, gezeichnet als
 * Stauchung in der Breite — so sieht Papier aus, das sich dreht).
 *
 * **Liegenbleiben:** Der Boden ist in Spalten von {@link COLUMN_WIDTH} geteilt,
 * jede merkt sich, wie hoch dort schon etwas liegt. Ein Stück landet auf der
 * höchsten Spalte unter sich und hebt die Spalten unter sich um seine Dicke.
 * **Wie Sand:** Ist es gleich neben ihm tiefer, als es selbst dick ist, rutscht
 * es dorthin ab, statt auf einer Spitze zu balancieren — so wird der Haufen
 * flach und natürlich. Würde es höher als `maxPile`, verblasst es beim
 * Aufsetzen: Der Haufen darf „Weiter geht's" nicht zudecken.
 */

/** Breite einer Boden-Spalte in px. */
export const COLUMN_WIDTH = 4

/** Wie schnell ein Stück seine Sinkgeschwindigkeit erreicht (s). */
const FALL_EASE = 0.35
/** Wie schnell der seitliche Schwung verfliegt (s). */
const DRIFT_EASE = 0.8
/** Wie lange ein gelandetes Stück braucht, um flach zu liegen (ms). */
const SETTLE_MS = 220
/** Wie lange ein Stück verblasst, das keinen Platz mehr findet (ms). */
const FADE_MS = 400
/**
 * **Liegend ist Papier flach:** Von der Seite gesehen zeigt ein liegendes Stück
 * nur einen Teil seiner Höhe. Gelandet staucht es darum auf diesen Anteil —
 * sonst füllte schon eine Lage den ganzen Haufen, und fast alles verblasste.
 */
export const LYING_FLATNESS = 0.5

export type PieceState = 'waiting' | 'falling' | 'settling' | 'resting' | 'fading' | 'gone'

export interface ConfettiPiece {
  shape: ConfettiShape
  color: string
  scale: number
  /** Mittelpunkt in px, bezogen auf die Fläche. */
  x: number
  y: number
  vx: number
  vy: number
  /** Sinkgeschwindigkeit in px/s. */
  terminal: number
  angle: number
  spin: number
  /** Kippen um die Längsachse — gezeichnet als `scaleX = cos(tilt)`. */
  tilt: number
  tiltSpeed: number
  swayAmp: number
  swayFreq: number
  swayPhase: number
  /** Ab wann es fällt (ms seit Beginn). */
  spawnAt: number
  state: PieceState
  /** Liegend: Breite und Dicke in px. */
  footprint: number
  thickness: number
  /** Der Winkel, in dem es flach liegt. */
  restAngle: number
  settle: number
  /** 0 = aufrecht, 1 = flach liegend (gezeichnet als Stauchung in der Höhe). */
  flat: number
  settleFromAngle: number
  settleFromTilt: number
  opacity: number
}

export interface ConfettiField {
  width: number
  height: number
  /** Wie hoch der Haufen höchstens werden darf (px). */
  maxPile: number
  /** Höhe des Haufens je Spalte (px). */
  columns: Float64Array
  pieces: ConfettiPiece[]
  /** Vergangene Zeit (ms). */
  time: number
}

/** Kleiner, schneller Zufall mit Startwert (mulberry32) — gleicher Wert, gleiches Bild. */
function seededRandom(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function createField({ width, height, maxPile }: { width: number; height: number; maxPile: number }): ConfettiField {
  return {
    width,
    height,
    maxPile,
    columns: new Float64Array(Math.max(1, Math.ceil(width / COLUMN_WIDTH))),
    pieces: [],
    time: 0,
  }
}

function lyingSize(shape: ConfettiShape, scale: number) {
  const long = Math.max(shape.width, shape.height) * scale
  // Eine Luftschlange liegt so dick wie ihr Strich, nicht wie ihr Bogen hoch ist.
  const short = shape.strokeWidth ? shape.strokeWidth * scale : Math.min(shape.width, shape.height) * scale
  return { footprint: long, thickness: Math.max(1.5, short * LYING_FLATNESS) }
}

interface DropOptions {
  x: number
  y: number
  shape: ConfettiShape
  color: string
  scale?: number
  /** Ohne Zufall: senkrecht, ohne Drehen und Pendeln — für Tests. */
  calm?: boolean
  spawnAt?: number
  random?: () => number
}

/** Legt ein Stück in die Fläche und gibt es zurück. */
export function dropPiece(field: ConfettiField, options: DropOptions): ConfettiPiece {
  const { x, y, shape, color, scale = 1, calm = false } = options
  const random = options.random ?? Math.random
  const { footprint, thickness } = lyingSize(shape, scale)
  const streamer = shape.kind === 'streamer'
  // Liegend zeigt die lange Seite waagerecht.
  const lying = shape.height > shape.width ? Math.PI / 2 : 0

  const piece: ConfettiPiece = {
    shape,
    color,
    scale,
    x,
    y,
    vx: calm ? 0 : (random() - 0.5) * 90,
    vy: calm ? 0 : (random() - 0.35) * 90,
    terminal: calm ? 140 : streamer ? 85 + random() * 35 : 120 + random() * 55,
    angle: calm ? lying : random() * Math.PI * 2,
    spin: calm ? 0 : (random() - 0.5) * (streamer ? 5 : 8),
    tilt: calm ? 0 : random() * Math.PI * 2,
    tiltSpeed: calm ? 0 : (random() < 0.5 ? -1 : 1) * (3 + random() * 6),
    swayAmp: calm ? 0 : 8 + random() * 14,
    swayFreq: 1.5 + random() * 2,
    swayPhase: random() * Math.PI * 2,
    spawnAt: options.spawnAt ?? field.time,
    state: 'waiting',
    footprint,
    thickness,
    restAngle: lying + (calm ? 0 : (random() - 0.5) * 0.4),
    settle: 0,
    flat: 0,
    settleFromAngle: 0,
    settleFromTilt: 0,
    opacity: 1,
  }
  field.pieces.push(piece)
  return piece
}

function pickKind(random: () => number): ConfettiKind {
  const kinds = Object.keys(CONFETTI_MIX) as ConfettiKind[]
  const total = kinds.reduce((sum, kind) => sum + CONFETTI_MIX[kind], 0)
  let roll = random() * total
  for (const kind of kinds) {
    roll -= CONFETTI_MIX[kind]
    if (roll < 0) return kind
  }
  return 'dot'
}

const pick = <T>(list: readonly T[], random: () => number): T => list[Math.floor(random() * list.length)]!

/**
 * Ein ganzer Regen: **erst ein Schwall** (60 % der Stücke in 0,75 s), **dann
 * ein Nachrieseln** über knapp zwei Sekunden. Alle beginnen oberhalb der Fläche
 * und fallen hinein.
 */
export function createConfetti({
  width,
  height,
  count,
  seed,
  maxPile,
}: { width: number; height: number; count: number; seed: number; maxPile: number }): ConfettiField {
  const field = createField({ width, height, maxPile })
  const random = seededRandom(seed)
  const burst = Math.round(count * 0.6)

  for (let i = 0; i < count; i++) {
    const kind = pickKind(random)
    dropPiece(field, {
      x: random() * width,
      y: -(10 + random() * 60),
      shape: pick(CONFETTI_SHAPES[kind], random),
      color: pick(CONFETTI_COLORS[kind], random),
      scale: 0.9 + random() * 0.4,
      spawnAt: i < burst ? random() * 750 : 750 + ((i - burst) / Math.max(1, count - burst)) * 1800 + random() * 120,
      random,
    })
  }
  return field
}

function columnRange(field: ConfettiField, x: number, footprint: number): [number, number] {
  const last = field.columns.length - 1
  const from = Math.min(last, Math.max(0, Math.floor((x - footprint / 2) / COLUMN_WIDTH)))
  const to = Math.min(last, Math.max(0, Math.floor((x + footprint / 2) / COLUMN_WIDTH)))
  return [from, to]
}

function supportAt(field: ConfettiField, x: number, footprint: number): number {
  const [from, to] = columnRange(field, x, footprint)
  let highest = 0
  for (let c = from; c <= to; c++) highest = Math.max(highest, field.columns[c]!)
  return highest
}

/** Wie hoch der Haufen an dieser Stelle ist (px). */
export function pileHeightAt(field: ConfettiField, x: number): number {
  const c = Math.min(field.columns.length - 1, Math.max(0, Math.floor(x / COLUMN_WIDTH)))
  return field.columns[c]!
}

function land(field: ConfettiField, piece: ConfettiPiece) {
  // **Wie Sand:** Ist es gleich neben dem Stück tiefer, als es dick ist, rutscht es dorthin.
  for (let i = 0; i < 60; i++) {
    const [from, to] = columnRange(field, piece.x, piece.footprint)
    const here = supportAt(field, piece.x, piece.footprint)
    const left = from > 0 ? field.columns[from - 1]! : Infinity
    const right = to < field.columns.length - 1 ? field.columns[to + 1]! : Infinity
    const lower = Math.min(left, right)
    if (!(lower < here - piece.thickness - 0.01)) break
    piece.x += left <= right ? -COLUMN_WIDTH : COLUMN_WIDTH
  }

  const support = supportAt(field, piece.x, piece.footprint)
  piece.y = field.height - support - piece.thickness / 2
  piece.vx = 0
  piece.vy = 0

  const top = support + piece.thickness
  if (top > field.maxPile) {
    piece.state = 'fading'
    return
  }
  const [from, to] = columnRange(field, piece.x, piece.footprint)
  for (let c = from; c <= to; c++) field.columns[c] = top

  // Auf dem kürzesten Weg flach legen.
  piece.restAngle += Math.round((piece.angle - piece.restAngle) / Math.PI) * Math.PI
  piece.settleFromAngle = piece.angle
  piece.settleFromTilt = Math.cos(piece.tilt)
  piece.settle = 0
  piece.state = 'settling'
}

/** Bewegt alles um `dtMs` weiter. */
export function stepConfetti(field: ConfettiField, dtMs: number): void {
  const dt = dtMs / 1000
  field.time += dtMs
  const seconds = field.time / 1000

  for (const piece of field.pieces) {
    switch (piece.state) {
      case 'waiting':
        if (field.time >= piece.spawnAt) piece.state = 'falling'
        break

      case 'falling': {
        piece.vy += (piece.terminal - piece.vy) * (1 - Math.exp(-dt / FALL_EASE))
        piece.vx *= Math.exp(-dt / DRIFT_EASE)
        const sway = piece.swayAmp * piece.swayFreq * Math.cos(piece.swayPhase + piece.swayFreq * seconds)
        piece.x = Math.min(field.width, Math.max(0, piece.x + (piece.vx + sway) * dt))
        piece.y += piece.vy * dt
        piece.angle += piece.spin * dt
        piece.tilt += piece.tiltSpeed * dt
        const support = supportAt(field, piece.x, piece.footprint)
        if (piece.y + piece.thickness / 2 >= field.height - support) land(field, piece)
        break
      }

      case 'settling': {
        piece.settle = Math.min(1, piece.settle + dtMs / SETTLE_MS)
        const ease = 1 - (1 - piece.settle) ** 3
        piece.angle = piece.settleFromAngle + (piece.restAngle - piece.settleFromAngle) * ease
        // Das Kippen läuft aus: gelandet liegt es in voller Breite.
        piece.tilt = Math.acos(Math.min(1, Math.max(-1, piece.settleFromTilt + (1 - piece.settleFromTilt) * ease)))
        piece.flat = ease
        if (piece.settle >= 1) piece.state = 'resting'
        break
      }

      case 'fading':
        piece.opacity = Math.max(0, piece.opacity - dtMs / FADE_MS)
        if (piece.opacity === 0) piece.state = 'gone'
        break

      default:
        break
    }
  }
}

/** Liegt alles still (oder ist verblasst)? Dann muss nichts mehr gezeichnet werden. */
export function isSettled(field: ConfettiField): boolean {
  return field.pieces.every(p => p.state === 'resting' || p.state === 'gone')
}

/**
 * **Das ruhige Bild** für reduzierte Bewegung: die Stücke verstreut über die
 * Fläche, wie im Figma-Einzelbild — nichts fällt, nichts liegt unten.
 */
export function scatterConfetti({ width, height, count, seed }: { width: number; height: number; count: number; seed: number }): ConfettiField {
  const field = createField({ width, height, maxPile: 0 })
  const random = seededRandom(seed)
  for (let i = 0; i < count; i++) {
    const kind = pickKind(random)
    const piece = dropPiece(field, {
      x: random() * width,
      y: random() * height * 0.75,
      shape: pick(CONFETTI_SHAPES[kind], random),
      color: pick(CONFETTI_COLORS[kind], random),
      scale: 1 + random() * 0.5,
      random,
    })
    piece.state = 'resting'
  }
  return field
}
